import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import GlobalContext from "../contexts/store";
import SimpleEventCard from "../components/cards/SimpleEventCard";
import EventCalendar, { eventDate } from "../components/EventCalendar";

export default function UpcomingEvents() {
  const { events, upcomingEvents } = useContext(GlobalContext);
  const [view, setView] = useState("calendar"); // "calendar" | "list"
  const [selectedKey, setSelectedKey] = useState(null);
  const [query, setQuery] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const source = useMemo(() => {
    const base = (events && events.length ? events : upcomingEvents) || [];
    return base.filter((e) => e?.slug && !isNaN(eventDate(e)));
  }, [events, upcomingEvents]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return source;
    return source.filter((e) => (e.name || "").toLowerCase().includes(q));
  }, [source, query]);

  const sortedList = useMemo(
    () => [...filtered].sort((a, b) => eventDate(a) - eventDate(b)),
    [filtered]
  );

  return (
    <div className="min-h-screen bg-ink-900 pt-[80px] text-white">
      <div className="mx-auto max-w-7xl px-4 py-12 md:px-10 md:py-16">
        <p className="mb-3 text-[12px] font-semibold uppercase tracking-eyebrow text-brand-blue-bright">
          What&apos;s on
        </p>
        <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
          <h1 className="text-4xl font-extrabold tracking-tight md:text-5xl">
            Upcoming Shows
          </h1>

          <div className="inline-flex rounded-lg border-[0.5px] border-white/10 p-1">
            {["calendar", "list"].map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setView(v)}
                className={`rounded-md px-4 py-2 text-sm font-semibold capitalize transition-colors ${
                  view === v
                    ? "bg-brand-blue text-white"
                    : "text-white/60 hover:text-white"
                }`}
              >
                {v}
              </button>
            ))}
          </div>
        </div>

        {/* Search */}
        <div className="relative mt-8 max-w-xl">
          <label htmlFor="events-search" className="sr-only">
            Search shows by name
          </label>
          <svg
            viewBox="0 0 24 24"
            className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-white/40"
            fill="none"
            aria-hidden
          >
            <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="1.6" />
            <path
              d="M20 20l-3.5-3.5"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
            />
          </svg>
          <input
            id="events-search"
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search shows by name…"
            className="h-12 w-full rounded-lg border-[0.5px] border-white/10 bg-white/[0.04] pl-12 pr-4 text-[15px] text-white placeholder:text-white/40 outline-none transition focus:border-brand-blue focus:ring-1 focus:ring-brand-blue"
          />
        </div>

        {source.length === 0 && (
          <p className="mt-12 text-white/50">
            No upcoming shows are listed right now. Check back soon.
          </p>
        )}

        {view === "calendar" && filtered.length > 0 && (
          <EventCalendar
            events={filtered}
            selectedKey={selectedKey}
            onSelectDay={setSelectedKey}
            className="mt-10"
          />
        )}

        {view === "calendar" && source.length > 0 && filtered.length === 0 && (
          <p className="mt-10 text-white/50">
            No shows match “{query.trim()}”.
          </p>
        )}

        {view === "list" && filtered.length > 0 && (
          <div className="mt-10 grid grid-cols-2 gap-x-4 gap-y-8 sm:gap-x-6 sm:gap-y-12 lg:grid-cols-4 lg:gap-x-8">
            {sortedList.map((ev) => (
              <Link
                key={ev._id || ev.slug}
                to={`/events/event/${ev.slug}`}
                className="block"
              >
                <SimpleEventCard
                  image={
                    ev.promo?.post || ev.promo?.story || ev.promo?.banner
                  }
                  title={ev.name}
                  location={eventDate(ev).toLocaleDateString("en-US", {
                    weekday: "short",
                    month: "short",
                    day: "numeric",
                  })}
                />
              </Link>
            ))}
          </div>
        )}

        {view === "list" && source.length > 0 && filtered.length === 0 && (
          <p className="mt-10 text-white/50">
            No shows match “{query.trim()}”.
          </p>
        )}
      </div>
    </div>
  );
}
